import { Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import {
  MonthPicker,
  monthLabel,
  type MonthKey,
} from "@/components/team/MonthPicker";
import type { TeamMemberRef } from "@/types/domain";

/**
 * Cabecera del tablero. Cambia de identidad según quién mira: para quien dirige es
 * "Mi equipo" y trae el acceso a administración; para el comercial es su bandeja.
 */
export function BoardHeader({
  isTeam,
  leadsTeam,
  memberCount,
  lineLabel,
  organizationName,
  month,
  months,
  onMonthChange,
  me,
  onManage,
}: {
  isTeam: boolean;
  leadsTeam: boolean;
  memberCount: number;
  /** `null` en Fianzas, que no trabaja con líneas. */
  lineLabel: string | null;
  /** Razón social de la empresa; sin ella se cae al rótulo genérico. */
  organizationName?: string | null;
  /** El mes del que hablan las métricas. Va en el título porque las ordena todas. */
  month: MonthKey;
  months: MonthKey[];
  onMonthChange: (month: MonthKey) => void;
  me?: TeamMemberRef | null;
  onManage?: () => void;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
          {organizationName ?? "CRM de licitaciones"}
        </p>
        <h1 className="flex flex-wrap items-center gap-x-1 text-3xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50">
          {leadsTeam ? "Mi equipo" : "Mis licitaciones"}
          <span className="text-slate-300 dark:text-slate-600">·</span>
          {months.length > 0 ? (
            <MonthPicker
              value={month}
              months={months}
              onChange={onMonthChange}
            />
          ) : (
            <span className="text-primary">{monthLabel(month)}</span>
          )}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {leadsTeam
            ? "Qué tiene cada persona, cuánto hay en juego y qué necesita movimiento hoy."
            : "Lo que tienes asignado, lo que vence y lo que puedes tomar de tus líneas."}
        </p>
      </div>

      {leadsTeam ? (
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">
            {lineLabel ? `${lineLabel} · ` : ""}
            {memberCount} persona{memberCount === 1 ? "" : "s"}
          </span>
          {onManage && (
            <Button size="sm" onClick={onManage} className="rounded-lg">
              <Users className="size-4" />
              Administrar equipo
            </Button>
          )}
        </div>
      ) : (
        isTeam &&
        me && (
          <div className="flex items-center gap-2 rounded-xl border bg-card px-3 py-2">
            <MemberAvatar member={me} className="size-7" />
            <span className="text-sm font-semibold">{me.name}</span>
            <Badge variant="secondary">Comercial</Badge>
          </div>
        )
      )}
    </div>
  );
}
